/**
 * DispatchTicker
 *
 * A "live" dispatch stat strip that plays under the homepage hero headline
 * to reinforce the "we deliver every day" narrative established by the
 * opening truck intro. Rotates through 4 stats every 3.5s with a pulsing
 * green LIVE dot for freshness.
 *
 * The numbers are deterministic-per-day (seeded on today's date) so the
 * same visitor sees consistent counts on refresh but the values shift
 * naturally across days.
 *
 * Not persisted to the DB — this is a marketing "reinforcement" widget,
 * not a source of truth. When we wire real ops data (from /api/orders,
 * /api/backorders), swap the seed function for a live fetch.
 */
import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Truck, Package, Clock, Zap } from "lucide-react";

// Deterministic pseudo-random based on today's date so numbers feel stable
// per visit but shift across days.
function daySeed() {
  const d = new Date();
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}
function seededRand(seed, min, max) {
  const x = Math.sin(seed) * 10000;
  const frac = x - Math.floor(x);
  return Math.floor(frac * (max - min + 1)) + min;
}

// Next scheduled Houston dispatch run: 6:00 AM if it's before 6, else
// the next same-day slot (10 AM / 2 PM / 6 PM), else tomorrow 6 AM.
function nextRunLabel() {
  const now = new Date();
  const h = now.getHours();
  const slots = [6, 10, 14, 18];
  const next = slots.find((s) => s > h);
  if (next == null) return "Tomorrow 6:00 AM";
  const ampm = next < 12 ? "AM" : "PM";
  const display = next <= 12 ? next : next - 12;
  return `${display}:00 ${ampm} Houston`;
}

export default function DispatchTicker() {
  const [i, setI] = useState(0);

  const stats = useMemo(() => {
    const seed = daySeed();
    const trucks = seededRand(seed, 2, 5);
    const orders = seededRand(seed + 7, 34, 68);
    return [
      { icon: Truck, label: `${trucks} trucks dispatched today` },
      { icon: Package, label: `${orders} orders shipped this week` },
      { icon: Clock, label: `Next Houston run · ${nextRunLabel()}` },
      { icon: Zap, label: "Same-day rush available · Gulf region" },
    ];
  }, []);

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % stats.length), 3500);
    return () => clearInterval(t);
  }, [stats.length]);

  const Cur = stats[i];
  const CurIcon = Cur.icon;

  return (
    <div
      className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-slate-900/50 backdrop-blur-md border border-cyan-500/25 shadow-lg shadow-cyan-500/10"
      data-testid="dispatch-ticker"
      role="status"
      aria-live="polite"
    >
      {/* Pulsing live dot */}
      <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      <span className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-300">
        Live
      </span>
      <span className="hidden sm:block w-px h-4 bg-white/15" />
      <AnimatePresence mode="wait">
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="flex items-center gap-2 text-sm text-white font-semibold"
          data-testid={`dispatch-ticker-stat-${i}`}
        >
          <CurIcon className="w-4 h-4 text-cyan-300 shrink-0" />
          <span>{Cur.label}</span>
        </motion.div>
      </AnimatePresence>
      {/* Dots pagination */}
      <div className="hidden md:flex items-center gap-1 ml-1" aria-hidden>
        {stats.map((_, idx) => (
          <span
            key={idx}
            className={`h-1 rounded-full transition-all ${
              idx === i ? "bg-cyan-300 w-3" : "bg-white/25 w-1"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
